import { useCallback, useEffect, useState } from "react";
import { api, errMsg, download } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MagnifyingGlass, Plus, PencilSimple, DownloadSimple, Eye, Power, Trash, Key, Printer, Copy, ArrowCounterClockwise, Umbrella, Warning } from "@phosphor-icons/react";
import EmployeeSummary from "@/components/EmployeeSummary";
import EmployeeCard from "@/components/EmployeeCard";
import { VacationDialog } from "@/components/VacationDialog";
import { IdDocsSection } from "@/components/IdDocsSection";

const EMPTY = {
  first_name: "", last_name: "", code: "", department: "", role_title: "", hire_date: "", active: true,
  company: "", tax_code: "", phone: "", email: "", birth_city: "", birth_province: "", birth_date: "",
  residence_city: "", residence_province: "", address: "", street_number: "",
  piecework: false, line: "",
};

const FORM_FIELDS = [
  ["last_name", "Cognome"], ["first_name", "Nome"], ["code", "Codice dipendente"],
  ["role_title", "Qualifica / Mansione"], ["tax_code", "Codice fiscale"], ["phone", "Telefono"],
  ["email", "Mail"], ["birth_city", "Città di nascita"], ["birth_province", "Provincia di nascita"],
  ["birth_date", "Data di nascita"], ["residence_city", "Comune di residenza"],
  ["residence_province", "Provincia residenza"], ["address", "Indirizzo di residenza"],
  ["street_number", "Numero civico"],
];

const CF_VALIDO = /^[A-Za-z]{6}[0-9A-Za-z]{10}$/;

/** Campi indispensabili mancanti o non validi in una scheda dipendente. */
export function mancanti(r) {
  const buchi = [];
  const cf = (r.tax_code || "").trim();
  if (!cf) buchi.push("codice fiscale");
  else if (!CF_VALIDO.test(cf)) buchi.push("codice fiscale non valido (16 caratteri)");
  if (!r.birth_date) buchi.push("data di nascita");
  if (!(r.department || "").trim()) buchi.push("reparto");
  if (!(r.role_title || "").trim()) buchi.push("qualifica");
  if (!(r.code || "").trim()) buchi.push("codice dipendente");
  return buchi;
}

export default function Employees() {  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canCredentials = ["admin", "manager"].includes(user?.role);
  const canHr = user?.role !== "rspp";
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [department, setDepartment] = useState("all");
  const [status, setStatus] = useState("all");
  const [form, setForm] = useState(null);
  const [summaryId, setSummaryId] = useState(null);
  const [cardId, setCardId] = useState(null);
  const [elencoIncompleti, setElencoIncompleti] = useState(false);
  const incomplete = rows.filter((r) => r.active && mancanti(r).length > 0);
  const [vacationOf, setVacationOf] = useState(null);
  const [credential, setCredential] = useState(null);
  const [deleted, setDeleted] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, d] = await Promise.all([
        api.get("/employees", { params: { q, department, status } }),
        api.get("/departments"),
      ]);
      setRows(e.data);
      setDepartments(d.data);
      api.get("/companies").then((c) => setCompanies(c.data)).catch(() => {});
      if (canCredentials) {
        const a = await api.get("/employee-accounts");
        setAccounts(a.data);
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [q, department, status, canCredentials]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const cf = (form.tax_code || "").trim().toUpperCase();
    if (cf && !CF_VALIDO.test(cf)) {
      toast.error("Codice fiscale non valido: servono 16 caratteri (6 lettere + 10 caratteri alfanumerici)");
      return;
    }
    const payload = { ...form, tax_code: cf, hire_date: form.hire_date || null };
    delete payload.id;
    try {
      if (form.id) {
        await api.put(`/employees/${form.id}`, payload);
        toast.success("Dipendente aggiornato");
        setForm(null);
        load();
        return;
      }
      const { data } = await api.post("/employees", payload);
      toast.success(`Dipendente creato con codice ${data.code}: ora puoi caricare i documenti identificativi`);
      // si resta nella scheda appena creata per caricare subito i documenti
      setForm({ ...EMPTY, ...data, hire_date: data.hire_date || "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggle = async (r) => {
    try {
      await api.patch(`/employees/${r.id}/status`, { active: !r.active });
      toast.success(!r.active ? "Dipendente attivato" : "Dipendente disattivato (storico conservato)");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeForever = async (r) => {
    const ok = window.confirm(
      `Eliminare ${r.last_name} ${r.first_name} (${r.code})?\n\n` +
      "Il dipendente viene spostato nell'archivio ELIMINATI: presenze, pesi e storico restano salvati " +
      "e potrai ripristinarlo in qualsiasi momento. L'account di accesso viene rimosso."
    );
    if (!ok) return;
    try {
      const { data } = await api.delete(`/employees/${r.id}`, { params: { permanent: true } });
      toast.success(data.message || "Dipendente spostato negli Eliminati");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openDeleted = async () => {
    try { setDeleted((await api.get("/employees/deleted")).data); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const restore = async (r) => {
    try {
      await api.post(`/employees/deleted/${r.id}/restore`);
      toast.success(`${r.last_name} ${r.first_name} ripristinato con tutto lo storico`);
      openDeleted();
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const accountOf = (r) => accounts.find((a) => a.employee_id === r.id);

  const makeCredentials = async (r) => {
    const existing = accountOf(r);
    try {
      if (existing) {
        if (!window.confirm(`${r.last_name} ${r.first_name} ha già l'account ${existing.email}. Generare una nuova password?`)) return;
        const { data } = await api.post(`/employee-accounts/${existing.id}/reset-password`);
        setCredential({ id: existing.id, email: data.email, password: data.temp_password, name: `${r.last_name} ${r.first_name}` });
      } else {
        const email = (r.email || `${r.first_name}.${r.last_name}`.toLowerCase().replace(/\s+/g, "") + "@azienda.it").toLowerCase();
        const { data } = await api.post("/employee-accounts", { employee_id: r.id, email });
        setCredential({ id: data.id, email: data.email, password: data.temp_password, name: `${r.last_name} ${r.first_name}` });
      }
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const printSheet = async (c) => {
    try {
      const res = await api.post(`/employee-accounts/${c.id}/credentials-pdf`,
        { password: c.password, site_url: window.location.origin }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="employees-page" className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Anagrafica Dipendenti</h1>
          <p className="text-sm text-muted-foreground mt-1">{rows.length} risultati · scheda completa, credenziali di accesso ed esportazioni</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canCredentials && ["csv", "xlsx", "pdf"].map((f) => (
            <Button key={f} variant="outline" size="sm" className="rounded-sm" data-testid={`export-employees-${f}`}
              onClick={() => download(`/export/employees?fmt=${f}`, `dipendenti.${f}`)}>
              <DownloadSimple size={14} className="mr-1.5" /> {f.toUpperCase()}
            </Button>
          ))}
          {isAdmin && (
            <Button variant="outline" size="sm" className="rounded-sm" data-testid="open-deleted-btn"
              onClick={openDeleted}>
              <ArrowCounterClockwise size={15} className="mr-1.5" /> Eliminati
            </Button>
          )}
          {isAdmin && (
            <Button size="sm" className="rounded-sm font-semibold" onClick={() => setForm({ ...EMPTY })} data-testid="add-employee-btn">
              <Plus size={15} className="mr-1.5" /> Nuovo dipendente
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-sm border border-border bg-card p-3 flex flex-wrap gap-2">
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="employee-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Nome, cognome, codice, reparto..." className="h-9 w-64 pl-8 rounded-sm" />
        </div>
        <Select value={department} onValueChange={setDepartment}>
          <SelectTrigger className="h-9 w-[170px] rounded-sm" data-testid="employee-department-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i reparti</SelectItem>
            {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-[150px] rounded-sm" data-testid="employee-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="active">Solo attivi</SelectItem>
            <SelectItem value="inactive">Solo non attivi</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {incomplete.length > 0 && (
        <div className="rounded-sm border border-rose-300 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/40 p-3 flex flex-wrap items-center gap-2"
          data-testid="incomplete-banner">
          <Warning size={18} className="text-rose-600 shrink-0" />
          <span className="text-sm">
            <b>{incomplete.length}</b> {incomplete.length === 1 ? "anagrafica incompleta" : "anagrafiche incomplete"}:
            mancano dati importanti (codice fiscale valido, data di nascita, reparto o qualifica).
          </span>
          <Button variant="outline" size="sm" className="h-8 rounded-sm font-semibold ml-auto"
            data-testid="incomplete-list-btn" onClick={() => setElencoIncompleti(true)}>
            Vedi elenco
          </Button>
        </div>
      )}

      <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">        <table className="w-full text-sm tbl-sticky" data-testid="employees-table">
          <thead className="bg-secondary">
            <tr>
              {["", "Codice", "Cognome e nome", "Azienda", "Qualifica", "Postazione", "Codice fiscale", "Telefono", "Accesso", "Stato"].map((h) => (
                <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="px-3 py-8 text-center text-muted-foreground">Caricamento...</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={10} className="px-3 py-8 text-center text-muted-foreground" data-testid="employees-empty">Nessun dipendente trovato</td></tr>}
            {rows.map((r) => {
              const acc = accountOf(r);
              return (
                <tr key={r.id} className="border-t border-border hover:bg-secondary/40" data-testid={`employee-row-${r.code}`}>
                  <td className="px-3 py-1.5">
                    <div className="flex justify-start gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7 rounded-sm" title="Scheda completa"
                        onClick={() => setCardId(r.id)} data-testid={`view-employee-${r.code}`}>
                        <Eye size={15} />
                      </Button>
                      {canHr && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-sm" title="Situazione ferie"
                          onClick={() => setVacationOf({ id: r.id, name: `${r.last_name} ${r.first_name}` })}
                          data-testid={`vacations-employee-${r.code}`}>
                          <Umbrella size={15} />
                        </Button>
                      )}
                      {canCredentials && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-sm text-primary"
                          title={acc ? "Rigenera credenziali di accesso" : "Crea credenziali di accesso"}
                          onClick={() => makeCredentials(r)} data-testid={`credentials-employee-${r.code}`}>
                          <Key size={15} />
                        </Button>
                      )}
                      {isAdmin && (
                        <>
                          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-sm" title="Modifica"
                            onClick={() => setForm({ ...EMPTY, ...r, hire_date: r.hire_date || "" })} data-testid={`edit-employee-${r.code}`}>
                            <PencilSimple size={15} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-sm" title={r.active ? "Disattiva" : "Attiva"}
                            onClick={() => toggle(r)} data-testid={`toggle-employee-${r.code}`}>
                            <Power size={15} className={r.active ? "text-rose-600" : "text-emerald-600"} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-sm text-destructive"
                            title="Elimina definitivamente (rimuove anche lo storico)"
                            onClick={() => removeForever(r)} data-testid={`delete-employee-${r.code}`}>
                            <Trash size={15} />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-1.5 tabular-nums font-medium">{r.code}</td>
                  <td className="px-3 py-1.5">
                    <button className="text-left hover:text-primary transition-colors duration-150" onClick={() => setCardId(r.id)}>
                      <span className="font-semibold">{r.last_name}</span> {r.first_name}
                    </button>
                    {r.piecework && (
                      <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300"
                        data-testid={`piecework-badge-${r.code}`}>
                        COTTIMO{r.line ? ` ${r.line}` : ""}
                      </span>
                    )}
                    {mancanti(r).length > 0 && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300"
                        title={`Anagrafica incompleta: ${mancanti(r).join(", ")}`}
                        data-testid={`incomplete-badge-${r.code}`}>
                        <Warning size={11} /> DA COMPLETARE
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-muted-foreground text-xs">{r.company || "—"}</td>
                  <td className="px-3 py-1.5 text-muted-foreground">{r.role_title}</td>
                  <td className="px-3 py-1.5 text-muted-foreground">{r.department}</td>
                  <td className="px-3 py-1.5 tabular-nums text-xs text-muted-foreground">{r.tax_code || "—"}</td>
                  <td className="px-3 py-1.5 tabular-nums text-xs text-muted-foreground">{r.phone || "—"}</td>
                  <td className="px-3 py-1.5 text-xs">
                    {acc
                      ? <span className="text-emerald-700 dark:text-emerald-400 font-semibold">{acc.must_change_password ? "Da attivare" : "Attivo"}</span>
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${r.active
                      ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-300"
                      : "bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-300"}`}>
                      {r.active ? "ATTIVO" : "NON ATTIVO"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={elencoIncompleti} onOpenChange={setElencoIncompleti}>
        <DialogContent className="rounded-sm sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="incomplete-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Anagrafiche da completare ({incomplete.length})</DialogTitle>
          </DialogHeader>
          <div className="divide-y divide-border">
            {incomplete.map((r) => (
              <div key={r.id} className="py-2 flex items-start justify-between gap-2"
                data-testid={`incomplete-item-${r.code}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{r.last_name} {r.first_name}
                    <span className="ml-2 text-[11px] text-muted-foreground tabular-nums">{r.code}</span>
                  </div>
                  <div className="text-xs text-rose-700 dark:text-rose-300">Manca: {mancanti(r).join(", ")}</div>
                </div>
                {isAdmin && (
                  <Button variant="outline" size="sm" className="h-8 rounded-sm shrink-0"
                    data-testid={`incomplete-fix-${r.code}`}
                    onClick={() => { setElencoIncompleti(false); setForm({ ...EMPTY, ...r, hire_date: r.hire_date || "" }); }}>
                    Completa
                  </Button>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {form && (
        <Dialog open onOpenChange={() => setForm(null)}>
          <DialogContent className="rounded-sm sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="employee-dialog">
            <DialogHeader><DialogTitle className="font-head">{form.id ? "Modifica dipendente" : "Nuovo dipendente"}</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3">
              {FORM_FIELDS.map(([k, l]) => (
                <div key={k}>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {k === "code" && !form.id ? "Codice dipendente (automatico)" : l}
                  </Label>
                  <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    placeholder={k === "code" && !form.id ? "assegnato automaticamente (es. DIP044)" : ""}
                    className={`h-9 rounded-sm mt-1 ${k === "tax_code" && (form.tax_code || "").trim()
                      && !CF_VALIDO.test((form.tax_code || "").trim()) ? "border-rose-500 text-rose-700" : ""}`}
                    data-testid={`form-${k}`} />
                  {k === "tax_code" && (form.tax_code || "").trim() && !CF_VALIDO.test((form.tax_code || "").trim()) && (
                    <p className="mt-1 text-[11px] font-semibold text-rose-600" data-testid="form-tax_code-error">
                      Formato non valido: 16 caratteri (6 lettere + 10 alfanumerici)
                    </p>
                  )}
                </div>
              ))}
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Postazione / Reparto</Label>
                <Input value={form.department || ""} onChange={(e) => setForm({ ...form, department: e.target.value })}
                  list="dep-list" className="h-9 rounded-sm mt-1" data-testid="form-department" />
                <datalist id="dep-list">{departments.map((d) => <option key={d} value={d} />)}</datalist>
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Data assunzione</Label>
                <Input type="date" value={form.hire_date || ""} onChange={(e) => setForm({ ...form, hire_date: e.target.value })}
                  className="h-9 rounded-sm mt-1" data-testid="form-hire_date" />
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Azienda di appartenenza</Label>
                <Select value={form.company || "none"} onValueChange={(v) => setForm({ ...form, company: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="form-company"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— nessuna —</SelectItem>
                    {companies.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Cottimista (a cottimo)</Label>
                <Select value={form.piecework ? "1" : "0"} onValueChange={(v) => setForm({ ...form, piecework: v === "1" })}>
                  <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="form-piecework"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">SÌ — lavoratore a cottimo</SelectItem>
                    <SelectItem value="0">NO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Linea di lavorazione</Label>
                <Select value={form.line || "none"} onValueChange={(v) => setForm({ ...form, line: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="form-line"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— nessuna —</SelectItem>
                    <SelectItem value="SUINO">SUINO</SelectItem>
                    <SelectItem value="BOVINO">BOVINO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Stato (assunto)</Label>
                <Select value={form.active ? "1" : "0"} onValueChange={(v) => setForm({ ...form, active: v === "1" })}>
                  <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="form-active"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">ATTIVO</SelectItem>
                    <SelectItem value="0">NON ATTIVO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.id ? (
              <div className="pt-1" data-testid="employee-dialog-id-docs">
                <IdDocsSection employeeId={form.id} />
              </div>
            ) : (
              <div className="rounded-sm border border-dashed border-border p-3 text-sm text-muted-foreground"
                data-testid="id-docs-hint">
                <span className="font-semibold text-foreground">Documenti Identificativi</span> — Documento di
                Identità e Tessera Sanitaria si caricano appena il dipendente è salvato: premi Salva e la sezione
                comparirà qui e nella sua scheda.
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setForm(null)} data-testid="employee-cancel">Annulla</Button>
              <Button className="rounded-sm font-semibold" onClick={submit} data-testid="employee-save">Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {cardId && (
        <EmployeeCard employeeId={cardId} onClose={() => setCardId(null)}
          onOpenSummary={canHr ? (id) => { setCardId(null); setSummaryId(id); } : null} />
      )}

      {summaryId && (
        <EmployeeSummary employeeId={summaryId} year={new Date().getFullYear()} month={new Date().getMonth() + 1}
          onClose={() => setSummaryId(null)} />
      )}

      {credential && (
        <Dialog open onOpenChange={() => setCredential(null)}>
          <DialogContent className="rounded-sm sm:max-w-md" data-testid="credential-dialog">
            <DialogHeader><DialogTitle className="font-head">Credenziali per {credential.name}</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">
              Stampa il foglio da consegnare al dipendente. Al primo accesso dovrà cambiare la password.
            </p>
            <div className="rounded-sm border border-border bg-secondary p-3 space-y-1.5 text-sm">
              <div><span className="text-muted-foreground">Sito:</span> <b>{window.location.origin}</b></div>
              <div><span className="text-muted-foreground">Email:</span> <b data-testid="credential-email">{credential.email}</b></div>
              <div><span className="text-muted-foreground">Password provvisoria:</span> <b className="tabular-nums" data-testid="credential-password">{credential.password}</b></div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" data-testid="credential-copy"
                onClick={() => { navigator.clipboard?.writeText(`${window.location.origin} — ${credential.email} / ${credential.password}`); toast.success("Credenziali copiate"); }}>
                <Copy size={14} className="mr-1.5" /> Copia
              </Button>
              <Button variant="outline" className="rounded-sm font-semibold" onClick={() => printSheet(credential)} data-testid="credential-print">
                <Printer size={14} className="mr-1.5" /> Stampa foglio PDF
              </Button>
              <Button className="rounded-sm font-semibold" onClick={() => setCredential(null)} data-testid="credential-close">Chiudi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {deleted && (
        <Dialog open onOpenChange={() => setDeleted(null)}>
          <DialogContent className="rounded-sm sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="deleted-dialog">
            <DialogHeader><DialogTitle className="font-head">Dipendenti eliminati</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">
              Nessun dato viene cancellato: presenze e pesi restano collegati. Con "Ripristina" il
              dipendente torna in anagrafica con tutto il suo storico.
            </p>
            <div className="rounded-sm border border-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary"><tr>
                  {["", "Codice", "Cognome e nome", "Postazione", "Presenze", "Pesate", "Eliminato il"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {deleted.map((r) => (
                    <tr key={r.id} className="border-t border-border" data-testid={`deleted-row-${r.id}`}>
                      <td className="px-3 py-1.5 text-left">
                        <Button size="sm" className="h-8 rounded-sm font-semibold"
                          data-testid={`restore-employee-${r.id}`} onClick={() => restore(r)}>
                          <ArrowCounterClockwise size={14} className="mr-1.5" /> Ripristina
                        </Button>
                      </td>
                      <td className="px-3 py-1.5 tabular-nums">{r.code}</td>
                      <td className="px-3 py-1.5"><b>{r.last_name}</b> {r.first_name}</td>
                      <td className="px-3 py-1.5 text-xs text-muted-foreground">{r.department}</td>
                      <td className="px-3 py-1.5 tabular-nums">{r.attendance_count}</td>
                      <td className="px-3 py-1.5 tabular-nums">{r.weights_count}</td>
                      <td className="px-3 py-1.5 text-xs text-muted-foreground">
                        {r.deleted_at ? new Date(r.deleted_at).toLocaleDateString("it-IT") : ""}
                      </td>
                    </tr>
                  ))}
                  {deleted.length === 0 && (
                    <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground" data-testid="deleted-empty">
                      Nessun dipendente eliminato
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button className="rounded-sm" onClick={() => setDeleted(null)} data-testid="deleted-close">Chiudi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {vacationOf && (
        <VacationDialog employeeId={vacationOf.id} name={vacationOf.name}
          onClose={() => setVacationOf(null)} />
      )}
    </div>
  );
}
